import { isReservationsBusinessType } from "./businessType.helper";

// Pure helpers for the appointments `businesses/{id}/services` subcollection.
//
// This file mirrors functions/src/utils/appointmentService.ts — the two must be kept
// in sync by hand, the same arrangement businessType.helper.js already uses. The
// functions copy is the one covered by unit tests (functions/src/utils/test/), since
// `app/` has no test runner.

// Optional fields are omitted from the Firestore payload when blank rather than
// written as null/undefined (Firestore rejects undefined, and null reads back as a
// real value). `clearedFields` lets an update delete them explicitly instead of
// leaving a stale value behind under `{ merge: true }`.
const OPTIONAL_TEXT_FIELDS = ["description"];
const OPTIONAL_NUMBER_FIELDS = ["price", "bufferBefore", "bufferAfter"];

const toTrimmedString = value => (typeof value === "string" ? value.trim() : "");

const toNumber = value => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

// Splits raw form values into the document to write and the optional fields that
// should be removed from an existing document.
export const buildServicePayload = (form = {}) => {
  const data = {
    name: toTrimmedString(form.name),
    duration: toNumber(form.duration) || 0,
    active: form.active !== false
  };
  const clearedFields = [];

  OPTIONAL_TEXT_FIELDS.forEach(field => {
    const value = toTrimmedString(form[field]);
    if (value) {
      data[field] = value;
    } else {
      clearedFields.push(field);
    }
  });

  OPTIONAL_NUMBER_FIELDS.forEach(field => {
    const value = toNumber(form[field]);
    // 0 and "no value" mean the same thing for a price or a buffer, so both clear.
    if (value !== null && value > 0) {
      data[field] = value;
    } else {
      clearedFields.push(field);
    }
  });

  return { data, clearedFields };
};

// A service with no explicit `active` flag is treated as active, matching the
// default the form writes.
export const filterActiveServices = (services = []) => services.filter(service => service?.active !== false);

export const sortServices = (services = []) =>
  [...services].sort((a, b) => {
    const orderA = typeof a?.sortOrder === "number" ? a.sortOrder : Number.MAX_SAFE_INTEGER;
    const orderB = typeof b?.sortOrder === "number" ? b.sortOrder : Number.MAX_SAFE_INTEGER;
    if (orderA !== orderB) {
      return orderA - orderB;
    }
    return toTrimmedString(a?.name).toLowerCase().localeCompare(toTrimmedString(b?.name).toLowerCase());
  });

export const formatServiceDuration = minutes => {
  const total = toNumber(minutes);
  if (total === null || total <= 0) {
    return "";
  }
  const hours = Math.floor(total / 60);
  const remainder = total % 60;
  if (!hours) {
    return `${remainder} min`;
  }
  return remainder ? `${hours} hr ${remainder} min` : `${hours} hr`;
};

// The appointments settings gate from the implementation plan (§4/§8): appointments
// UI is for non-restaurant/bar accounts that have already turned online booking on.
export const areAppointmentSettingsVisible = (accountType, settings) =>
  !isReservationsBusinessType(accountType) && !!settings?.selfBooking;
